import { debounce } from 'lodash';
import React, { Fragment, useCallback } from 'react';

import { useLink } from '@zapier/common-hooks';
import {
  Alert,
  AlertDescription,
  ButtonGroup,
  Margin,
} from '@zapier/design-system';
import { Icon } from '@zapier/zinnia-icons';

import AddInputButton from 'app/developer-v3/pages/actions/ActionInputsList/AddInputButton';
import ClickableTable from 'app/developer-v3/components/ClickableTable';
import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import SortControls from 'app/developer-v3/components/SortControls';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';
import {
  ACTION_INPUT_ROUTES as INPUT_ROUTES,
  ACTION_INPUT_TYPES as INPUT_TYPES,
  ACTION_INPUT_TYPE_TEXTS as INPUT_TYPE_TEXTS,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import {
  getActionUrl,
  getInputFields,
  getInputFieldType,
  isLastDescendantNodeClicked,
  reorderList,
} from 'app/developer-v3/utils';

import type { ActionParams, ActionSchema } from 'app/developer-v3/types';
import type { FieldOrFunctionSchema } from 'app/developer-v3/platformSchema/field';

const INPUT_CHOICES = [
  {
    label: INPUT_TYPE_TEXTS[INPUT_TYPES.INPUT],
    value: `/${INPUT_ROUTES[INPUT_TYPES.INPUT]}`,
  },
  {
    label: INPUT_TYPE_TEXTS[INPUT_TYPES.DYNAMIC],
    value: `/${INPUT_ROUTES[INPUT_TYPES.DYNAMIC]}`,
  },
  {
    label: INPUT_TYPE_TEXTS[INPUT_TYPES.GROUP],
    value: `/${INPUT_ROUTES[INPUT_TYPES.GROUP]}`,
  },
];

type Props = {
  action: ActionSchema;
  deleteInputField: (fieldIndex: number) => void;
  isSaving: boolean;
  params: ActionParams;
  saveActionChanges: (skipConfirm: boolean) => void;
  updateInputFields: (fields: FieldOrFunctionSchema[]) => void;
};

const ActionInputsList = (props: Props) => {
  const {
    action,
    deleteInputField,
    isSaving,
    params,
    saveActionChanges,
    updateInputFields,
  } = props;

  const { routeTo } = useLink();

  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  const getCellModifiers = useCallback(
    () => index => {
      return {
        wrap: index > 1,
        darken: index < 2,
      };
    },
    []
  );

  // Automated migration: this file was previously ignored by ESLint. Fix this violation if you touch this area of the code.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSaveActionChanges = useCallback(
    debounce(() => {
      saveActionChanges(true);
    }, 2000),
    [saveActionChanges]
  );

  const reorderFields = useCallback(
    (inputIndex: number, direction: number) => {
      const inputs = getInputFields(action);
      const newIndex = inputIndex + direction;
      const updatedFields = reorderList(inputs, inputIndex, newIndex);
      updateInputFields(updatedFields);
    },
    [action, updateInputFields]
  );

  const inputFields = getInputFields(action);

  // TODO: Is this dead code? It seems useful before but not used by ClickableTable atm.
  // const meta = { inputFields };

  const onChooseInputType = useCallback(
    option => {
      const baseUrl = `${getActionUrl(params)}/input/${NEW_SERVICE_FIELD}`;
      routeTo(`${baseUrl}${option.value}`);
    },
    [params, routeTo]
  );

  const onClickRow = useCallback(
    (event, index: number) => {
      if (!isLastDescendantNodeClicked(event)) {
        const fieldType = getInputFieldType(inputFields[index]);
        const inputUrl = `${getActionUrl(params)}/input/${index}/${
          INPUT_ROUTES[fieldType]
        }`;
        routeTo(inputUrl);
      }
    },
    [params, routeTo, inputFields]
  );

  return (
    <ManagerLayout shouldLimitWidth={true}>
      {inputFields.length === 0 ? (
        <Alert
          status="info"
          title="No input fields have been added to this Action yet"
        >
          <AlertDescription>
            Zapier Actions include a form where users map Trigger and other
            Action input fields to send data to your API. Add each field needed
            to create this Action item in your integration.
          </AlertDescription>
        </Alert>
      ) : (
        <ClickableTable
          body={inputFields.map((inputField, index, all) => {
            const type = getInputFieldType(inputField);
            const typeText = INPUT_TYPE_TEXTS[type];
            const label = String(
              inputField.label ||
                (type === 'dynamic'
                  ? INPUT_TYPE_TEXTS[INPUT_TYPES.DYNAMIC]
                  : '')
            );
            const key = String(inputField.key || '');
            return [
              { value: typeText, element: typeText },
              { value: label, element: label },
              { value: key, element: key },
              {
                value: '',
                element: (
                  <SortControls
                    isDisabledMoveDown={isSaving || index === all.length - 1}
                    isDisabledMoveUp={isSaving || index === 0}
                    onMoveDown={() => {
                      reorderFields(index, 1);
                      debouncedSaveActionChanges();
                    }}
                    onMoveUp={() => {
                      reorderFields(index, -1);
                      debouncedSaveActionChanges();
                    }}
                  />
                ),
              },
              {
                value: '',
                element: !isReadOnly && (
                  <ActionsDropdownMenu
                    items={[
                      {
                        content: (
                          <Fragment>
                            <Icon name="actionEdit" size={16} /> Edit
                          </Fragment>
                        ),
                        onChoose: () =>
                          routeTo(
                            `${getActionUrl(params)}/input/${index}/${
                              INPUT_ROUTES[type]
                            }`
                          ),
                        value: 'edit',
                      },
                      {
                        isConfirmationRequired: true,
                        isDanger: true,
                        content: (
                          <Fragment>
                            <Icon name="actionTrash" size={16} /> Delete
                          </Fragment>
                        ),
                        modalTextContent: `Are you sure you want to delete the input field with key: ${key}`,
                        modalTitle: 'Delete input field',
                        onChoose: () => deleteInputField(index),
                        value: 'delete',
                      },
                    ]}
                  />
                ),
              },
            ];
          })}
          getCellModifiers={getCellModifiers()}
          header={[
            { title: 'Type', hasSort: false },
            { title: 'Label', hasSort: false },
            { title: 'Key', hasSort: false },
            { title: 'Sort', hasSort: false },
            { title: '', hasSort: false },
          ]}
          onClickRow={onClickRow}
          rowAriaLabel="Click to edit this input field"
        />
      )}
      <Margin margin="20px 0px 0px">
        <ButtonGroup justify="end">
          <AddInputButton
            choices={INPUT_CHOICES}
            isDisabled={isReadOnly}
            onChoose={onChooseInputType}
          />
        </ButtonGroup>
      </Margin>
    </ManagerLayout>
  );
};

export default ActionInputsList;
